import { Button, DataTable, Progress, StatusBadge } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StorageApp } from '../../schemas/storage.schema';
import { formatBytes } from '../apps/AppList';

const NEAR = 80;

export function StorageByAppTable({
  rows,
  loading,
  onSelectApp,
}: {
  rows: StorageApp[];
  loading: boolean;
  onSelectApp: (appId: string) => void;
}) {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<StorageApp>[]>(
    () => [
      {
        accessorKey: 'appName',
        header: 'App',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() => onSelectApp(row.original.appId)}
            >
              {row.original.appName}
            </Button>
            {row.original.status === 'ORPHAN' ? (
              <StatusBadge tone="danger">Orphan</StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        id: 'usage',
        header: 'Used',
        cell: ({ row }) => {
          const a = row.original;
          const pct = a.quotaUsedPercent;
          return (
            <div className="min-w-40 space-y-1">
              <span className="text-sm tabular-nums">
                {formatBytes(a.bytesUsed)}
                {a.quotaBytes !== null ? ` / ${formatBytes(a.quotaBytes)}` : ''}
              </span>
              {pct !== null ? (
                <Progress
                  value={pct}
                  aria-label={`${a.appName}: ${pct}% of quota`}
                  className={pct >= NEAR ? '[&>div]:bg-destructive' : undefined}
                />
              ) : (
                <span className="block text-xs text-muted-foreground">no quota</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'percent',
        header: '%',
        cell: ({ row }) => {
          const pct = row.original.quotaUsedPercent;
          if (pct === null) return <span className="text-sm text-muted-foreground">—</span>;
          return (
            <StatusBadge tone={pct >= NEAR ? 'danger' : 'success'} dot={false}>
              {pct}%
            </StatusBadge>
          );
        },
      },
      { accessorKey: 'fileCount', header: 'Files' },
      {
        id: 'pending',
        header: 'Pending',
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {row.original.pendingCount}
            {row.original.stalePendingCount > 0 ? (
              <span className="text-destructive"> ({row.original.stalePendingCount} expired)</span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'added7',
        header: '+7d',
        cell: ({ row }) => (
          <span className="text-sm">{formatBytes(row.original.bytesAdded7d)}</span>
        ),
      },
      {
        id: 'added30',
        header: '+30d',
        cell: ({ row }) => (
          <span className="text-sm">{formatBytes(row.original.bytesAdded30d)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/files?app=${row.original.appId}`)}
          >
            Files
          </Button>
        ),
      },
    ],
    [onSelectApp, navigate],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={loading}
      hidePagination
      aria-label="Storage by app"
      empty={<p className="py-6 text-center text-sm text-muted-foreground">No apps yet</p>}
    />
  );
}
